import { useState, useEffect } from "react";
import { mutate } from "swr";
import axios from "axios";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ErrorMessage from "../../components/common/ErrorMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getValueInCurrency } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import { BackForeColor } from "../../components/common/BackForeColor";
import ModalBackground from "../../components/common/ModalBackground";
import {
    AdditionalStatusCodeEnum,
    getAdditionalCostStatusName,
    getAdditionalCostStatusBackColor,
    getAdditionalCostStatusForeColor,
    getAdditionalCostRequestFilteringOptions,
    getCurrencyFromSetup,
    getUserName,
} from "../../common/EntitiesManagement";
import { ADDITIONAL_COST_API, EVENTS_API } from "../../services/config";
import AddOrEditAdditionalCost from "./AddOrEditAdditionalCost";
import LoadingPanel from "../../components/common/LoadingPanel";

export default function AdditionalCostRequestList() {
    const {
        config,
        additionalCostRequestData,
        additionalCostRequestError,
        pageIndexAdditionalCostRequest,
        setPageIndexAdditionalCostRequest,
        setBasicFilterOptions,
    } = useGlobalContext();

    useEffect(() => {
        setBasicFilterOptions(getAdditionalCostRequestFilteringOptions());
    }, []);

    const normalizedData = Array.isArray(additionalCostRequestData)
        ? { Data: additionalCostRequestData, Count: additionalCostRequestData.length }
        : additionalCostRequestData;

    const [errorMessage, setErrorMessage] = useState("");
    const [showErrorModal, setShowErrorModal] = useState(false);

    useEffect(() => {
        if (additionalCostRequestError) {
            setErrorMessage(additionalCostRequestError?.message || localize.error_unexpected);
            setShowErrorModal(true);
        }
    }, [additionalCostRequestError]);
    const [additionalCostModel, setAdditionalCostModel] = useState(null);
    const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);

    const getColumnTitles = () => [
        localize.additional_cost_type,
        localize.value,
        localize.status,
        localize.observations,
        localize.request_date,
        localize.reported_by,
    ];

    const handleUpdateAdditionalCost = async () => {
        try {
            const data = await axios.put(
                `${EVENTS_API}/${additionalCostModel.eventId ?? additionalCostModel.EventId}/additionalCost/${additionalCostModel.Id}`,
                {
                    Value: additionalCostModel.Value,
                    AdditionalCostTypeId: additionalCostModel.AdditionalCostType?.Id ?? 0,
                    EventId: additionalCostModel.eventId ?? additionalCostModel.EventId,
                    CreatedBy: getUserName(),
                    Status: additionalCostModel.Status,
                    Observation: additionalCostModel.Observation
                },
                config
            );

            if (data?.data?.Ok) {
                mutate([
                    ADDITIONAL_COST_API,
                    {
                        ...config,
                        headers: {
                            ...config.headers,
                            pagination: pageIndexAdditionalCostRequest,
                        },
                    },
                ]);
                window.location.reload(false);
            } else {
                setErrorMessage(JSON.stringify(data?.data?.Data || data?.data?.data));
                setShowErrorModal(true);
            }
        } catch (error) {
            setErrorMessage(error.message);
            setShowErrorModal(true);
        }
        setShowStatusChangeModal(false);
    };

    if (!additionalCostRequestData && !additionalCostRequestError) {
        return <LoadingPanel />;
    }

    return (
        <div className="lateral_padding">
            {showErrorModal && (
                <ErrorMessage
                    message={errorMessage}
                    title={localize.error_unexpected}
                    showModal={showErrorModal}
                    setShowModal={setShowErrorModal}
                />
            )}

            {showStatusChangeModal && additionalCostModel && (
                <ModalBackground
                    isDrawerMode
                    hasRequiredValues
                    showModal={showStatusChangeModal}
                    setShowModal={setShowStatusChangeModal}
                    title={localize.change_additional_cost_status}
                    onClickToCancel={() => {
                        setShowStatusChangeModal(false);
                    }}
                    onClickToSubmit={() => {
                        handleUpdateAdditionalCost();
                    }}
                    Component={
                        <div className="mb-4">
                            <AddOrEditAdditionalCost
                                model={additionalCostModel}
                                setModel={setAdditionalCostModel}
                                setStatus={true}
                            />
                        </div>
                    }
                    validators={
                        additionalCostModel?.Status === AdditionalStatusCodeEnum.InProgress ||
                        (additionalCostModel?.Status === AdditionalStatusCodeEnum.Rejected &&
                            (!additionalCostModel?.Observation || additionalCostModel.Observation.length === 0))
                    }
                />
            )}

            <Card
                variant="outlined"
                style={{
                    margin: "16px",
                    boxShadow:
                        "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
                }}
            >
                <CardContent>
                    <PageTitleSubtitle
                        title={localize.additional_cost_request_title}
                        subtitle={localize.additional_cost_request_description}
                    />
                    <SearcherAndAdd hideAddButton />
                </CardContent>
            </Card>

            <TableComponent
                pagination={
                    <PagePagination
                        pageIndex={pageIndexAdditionalCostRequest}
                        setPageIndex={setPageIndexAdditionalCostRequest}
                        dataArray={normalizedData}
                    />
                }
                firstColumnTitle={localize.actions}
                columnTitles={getColumnTitles()}
                results={
                    normalizedData?.Data?.length > 0
                        ? normalizedData.Data.map((item) => ({
                            key: item.Id,
                            data: [
                                <div>
                                    <ActionButtons
                                        hideDeleteButton
                                        detailsButtonText={localize.additional_cost_request_view}
                                        onClickForDetailButton={() => {
                                            window.open(
                                                `/events/detail/${item.eventId}?tab_id=9`,
                                                "_blank"
                                            );
                                        }}
                                        hideEditButton={
                                            item.Status !== AdditionalStatusCodeEnum.InProgress
                                        }
                                        onClickForEditButton={() => {
                                            setAdditionalCostModel({
                                                ...item,
                                                AdditionalCostTypeId: item.AdditionalCostType?.Id,
                                            });
                                            setShowStatusChangeModal(true);
                                        }}
                                    />
                                </div>,
                                <ValueOrNoDataLabel data={item?.AdditionalCostType?.Name} />,
                                <ValueOrNoDataLabel data={getValueInCurrency(getCurrencyFromSetup(), item?.Value)} />,
                                <BackForeColor
                                    backColor={getAdditionalCostStatusBackColor(item.Status)}
                                    foreColor={getAdditionalCostStatusForeColor(item.Status)}
                                    text={getAdditionalCostStatusName(item.Status)}
                                />,
                                <ValueOrNoDataLabel data={item?.Observation} />,
                                new Date(item.CreationDate).toLocaleString(undefined, {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                }),
                                <ValueOrNoDataLabel data={item?.CreatedBy} />,
                            ],
                        }))
                        : [generateNoDataValues(7)]
                }
            />
        </div>
    );
}
